import _ from 'lodash';
import { Record as record, List } from 'immutable';
import moment from 'moment-timezone';

import Fares from './Fares';
import Journey from './Journey';

import { construct, fields, resolve } from '../helpers/LinkMapper';
import { diffDuration, durationString } from '../helpers/Durations';
import { ReservableStates, TransitModes } from '../constants/AppConstants';
import trainTime from '../lib/TrainTime';
import config from '../config';
import { translate as t } from '../lib/I18n';
import { getBrandInfo } from '../lib/brand';

const defaults = {
    id: null,
    journey: new Journey(),
    fares: new Fares(),
    oysterPrice: null
};

class Service extends record(defaults) {

    static fromJSON (json) {
        return construct(Service, json, {
            id: json.id || json.journey,
            journey: resolve.as(Journey),
            fares: resolve.as(Fares),
            oysterPrice: fields.number
        });
    }

    isSeatReservable () {
        return this.isSeatAvailable() && !this.isSeatCompulsory();
    }

    isSeatAvailable () {
        return this.getIn([ 'journey', 'legs' ])
            .filter((leg) => (
                (leg.serviceDetails.mode === TransitModes.TRAIN) &&
                !leg.serviceDetails.isCountedPlace))
            .map((leg) => leg.getIn([ 'serviceDetails', 'reservable' ]))
            .some((state) => state !== ReservableStates.NO);
    }

    isSeatCompulsory () {
        return this.getIn([ 'journey', 'legs' ])
            .filterNot((leg) => leg.getIn([ 'serviceDetails', 'isCountedPlace' ]))
            .map((leg) => leg.getIn([ 'serviceDetails', 'reservable' ]))
            .some((state) => state === ReservableStates.COMPULSORY);
    }

    hasCountedPlaceWarning () {
        const tocs = config.get('showCountedPlaceWarnings');
        if (!tocs || tocs.length === 0) {
            return false;
        }

        const countedPlaces = this.journey.getCountedPlaceLegs();
        return countedPlaces.some(leg => {
            const isCompulsory =
                leg.getIn(['serviceDetails', 'reservable']) === ReservableStates.COMPULSORY;
            const tocCode = leg.getIn(['serviceDetails', 'toc', 'code']);
            return isCompulsory && tocs.includes(tocCode);
        });
    }

    getCheapestTotalPrice () {
        return this.fares.getCheapestTotalPrice();
    }

    getFaresWithout (fare) {
        return this.fares.without(fare);
    }

    getCheapestForDirection (direction) {
        return this.fares.getCheapestForDirection(direction);
    }

    getCheapestInwardSingle () {
        return this.fares.getCheapestInwardSingle();
    }

    getCheapestInwardSinglePrice () {
        return this.fares.getCheapestInwardSinglePrice();
    }

    isValidWithFare (fare) {
        const fareType = fare.isReturn() ? 'returns' : 'singles';
        return !!this.fares.get(fareType).find((serviceFare) => serviceFare.id === fare.id);
    }

    isAdjustedDepartureTime () {
        return _.result(this.get('journey'), 'isAdjustedDepartureTime');
    }

    isAdjustedArrivalTime () {
        return _.result(this.get('journey'), 'isAdjustedArrivalTime');
    }

    getDepartureDelay () {
        return this.journey.getDepartureDateTime().calendarDiff(this.journey.getDepartureDateTime(false));
    }

    getArrivalDelay () {
        return this.journey.getArrivalDateTime().calendarDiff(this.journey.getArrivalDateTime(false));
    }

    isDelayed () {
        return moment.duration(this.getArrivalDelay()).as('minutes') > config.get('minServiceDelay');
    }

    getDepartureDateTime (adjusted = true) {
        return this.journey.getDepartureDateTime(adjusted);
    }

    getArrivalDateTime (adjusted = true) {
        return this.journey.getArrivalDateTime(adjusted);
    }

    getTripDuration () {
        return this.journey.getTripDuration();
    }

    getReplacementBusCount () {
        return this.getIn([ 'journey', 'legs' ]).filter((leg) => {
            return leg.getIn([ 'serviceDetails', 'mode' ]) === TransitModes.REPLACEMENT_BUS;
        }).size;
    }

    getOriginStation () {
        return this.getIn(['journey', 'origin', 'station']);
    }

    getDestinationStation () {
        return this.getIn(['journey', 'destination', 'station']);
    }

    getAllReturnTravelcards () {
        return this.fares.returns.filter(
            (item) => item.ticketType.isReturnTravelcard());
    }

    hasReturnTravelcards () {
        return this.fares.returns.some(
            fare => fare.ticketType.isReturnTravelcard()
        );
    }

    getChanges () {
        return this.getIn(['journey', 'changes']);
    }

    hasReplacementBus () {
        return this.getReplacementBusCount() !== 0;
    }

    isHistoric () {
        return _.result(this.getDepartureDateTime(), 'isBefore');
    }

    isOvertaken () {
        return this.getIn([ 'journey', 'isOvertaken' ]);
    }

    isCollectable () {
        const minTodOffset = this.getMinTodOffset();
        const collectionStart = trainTime.duration(config.get(minTodOffset));
        return trainTime().add(collectionStart).isBefore(this.getDepartureDateTime());
    }

    isCollectableSmartcard () {
        const collectionStart = trainTime.duration(config.get('minSmartcardOffset'));
        return trainTime().add(collectionStart).isBefore(this.getDepartureDateTime());
    }

    isBeforeServiceArrival (service) {
        if (service) {
            return this.getDepartureDateTime().isBefore(service.getArrivalDateTime());
        } else {
            return false;
        }
    }

    operators () {
        return this.journey.operators();
    }

    includesBrandToc () {
        return this.operators().some((toc) => toc.code === config.get('tocCode'));
    }

    isDisabled () {
        const journeyStartDate = this.journey.getDepartureDateTime();
        const tocs = this.operators();

        const disabledServices = new List(config.get('disabledServices'));

        const disabled = disabledServices.filter(({ tocCode, startDate, endDate }) => {
            const matchTOC = ((toc) => toc.code === tocCode);

            return tocs.some(matchTOC) && journeyStartDate.isBetween(startDate, endDate);
        });

        if (disabled.size) {
            return t(disabled.first().message, { moreInfo: config.get('bannerMessageURL') });
        }

        return false;
    }

    isCancelled () {
        return _.result(this.get('journey'), 'isCancelled');
    }

    isDisrupted () {
        return this.journey.isDisrupted();
    }

    isDisabledForTrip (trip) {
        return _.some([
            this.isDisabled(),
            this.isCancelled(),
            this.isHistoric(),
            !this.isCollectable(),
            !this.isCollectableSmartcard() && trip.isSmartcard()
        ]);
    }

    getCancellationMessage () {
        return this.journey.getCancellationMessage();
    }

    hasBulletins () {
        return _.result(this.get('journey'), 'hasBulletins');
    }

    hasFareCategoryAndMatchingRouteAndPrice (category, route, totalPrice) {
        return this.fares.getAllFares().some((fare) =>
            fare.category === category &&
            fare.route.code === route.code &&
            fare.totalPrice >= totalPrice
        );
    }

    getBulletins () {
        return _.result(this.get('journey'), 'bulletins');
    }

    hasP1bulletin () {
        return _.result(this.get('journey'), 'hasP1bulletin');
    }

    getJourneyDateDescription () {
        return t('booking.journey.srDateDescription', {
            scheduledDepartureDate: this.getDepartureDateTime(false).format('ddd DD MMM YYYY'),
            scheduledDeparture: this.getDepartureDateTime(false).format('HH:mm'),
            scheduledArrival: this.getArrivalDateTime(false).format('HH:mm'),
        });
    }

    getShortDescription () {
        return t('booking.journey.srShortDescription', {
            scheduledDeparture: this.getDepartureDateTime(false).format('HH:mm'),
            origin: t('booking.journey.stationName', this.getOriginStation().toJS()),
            scheduledArrival: this.getArrivalDateTime(false).format('HH:mm'),
            destination: t('booking.journey.stationName', this.getDestinationStation().toJS())
        });
    }

    getLongDescription () {
        const cancelled = this.isCancelled();
        const adjusted = this.isAdjustedArrivalTime();
        const delayed = this.journey.isArrivalDelayed() || this.journey.isIndeterminateDelay();
        const scheduledDeparture = this.getDepartureDateTime(false);
        const scheduledArrival = this.getArrivalDateTime(false);
        const duration = diffDuration(scheduledDeparture, scheduledArrival);
        let delayText = '';
        if (cancelled) {
            delayText = t('booking.service-carousel.item.cancelled');
        } else if (!cancelled && delayed && adjusted) {
            const delayString = diffDuration(scheduledArrival, this.getArrivalDateTime());
            delayText = t('booking.journey.delay', { delay: durationString(delayString, {type: 'long'}) });
        } else if (!cancelled && delayed && !adjusted) {
            delayText = t('booking.service-carousel.item.delayed');
        } else if (!cancelled && !delayed && !adjusted) {
            delayText = t('booking.service-carousel.item.on-time');
        }

        const longDescription = t('booking.journey.srLongDescription', {
            scheduledDeparture: scheduledDeparture.format('HH:mm'),
            origin: t('booking.journey.stationName', this.getOriginStation().toJS()),
            scheduledArrival: scheduledArrival.format('HH:mm'),
            destination: t('booking.journey.stationName', this.getDestinationStation().toJS()),
            duration: durationString(duration, {type: 'long'}),
            changes: t('booking.journey.srChanges', {changes: this.getChanges()}),
            delay: delayText
        });

        return !cancelled && delayed && adjusted
            ? `${longDescription} ${t('booking.journey.srAdjustedTimes', {
                adjustedDeparture: this.getDepartureDateTime().format('HH:mm'),
                adjustedArrival: this.getArrivalDateTime().format('HH:mm')
            })}`
            : longDescription;
    }

    getMinTodOffset() {
        const { isGTR } = getBrandInfo();
        return isGTR ? 'minTodGTROffset' : 'minTodOffset';
    }

    hasOtherAsServiceOperator () {
        const { isGTR } = getBrandInfo();

        return isGTR ? this.getIn([ 'journey', 'legs' ]).some(leg => leg.getIn(['serviceDetails', 'mode']) === 'Other') : false;
    }
}

export default Service;



// WEBPACK FOOTER //
// ./src/records/Service.js